import { useEffect, useState } from "react"
import moment from "moment"
import { X } from "lucide-react"

import { db } from "../../../../utils/firebase"
import Loading from "../../../../components/app/loading/loading"

/**
 * Read-only "view enquiry" modal, opened from the quote builder header so the
 * agent can see the originating enquiry (client details, trip parameters and -
 * crucially - the client notes) without leaving the builder.
 *
 * Fetches the enquiry + its client on open; renders nothing editable. Stacks
 * above the builder via the shared .package-modal overlay (z-index 1100).
 */
export default function EnquiryViewModal({ enquiryID, onClose }) {
	const [loading, setLoading] = useState(true)
	const [enquiry, setEnquiry] = useState(null)
	const [client, setClient] = useState(null)

	useEffect(() => {
		let cancelled = false
		const load = async () => {
			try {
				const enquirySnap = await db.doc(`enquiries/${enquiryID}`).get()
				const data = enquirySnap.exists ? enquirySnap.data() : null
				let clientData = null
				if (data?.client) {
					const clientSnap = await db.doc(`clients/${data.client}`).get()
					clientData = clientSnap.exists ? clientSnap.data() : null
				}
				if (cancelled) return
				setEnquiry(data)
				setClient(clientData)
			} catch (err) {
				console.error("Could not load enquiry:", err)
			} finally {
				if (!cancelled) setLoading(false)
			}
		}
		load()
		return () => {
			cancelled = true
		}
	}, [enquiryID])

	// Deliberately NO Escape / outside-click close, matching the other quote
	// modals - the X / action buttons are the only ways out.

	const clientName = client ? [client.first_name, client.last_name].filter(Boolean).join(" ").trim() : ""
	const clientEmail = client?.email || ""
	const clientPhone = client?.phone || client?.telephone || ""
	const travelLabel = enquiry?.date_from?.seconds ? moment(enquiry.date_from.seconds, "X").format("ddd D MMM YYYY") : ""

	const rows = enquiry
		? [
				{ label: "Email", value: clientEmail, href: clientEmail ? `mailto:${clientEmail}` : null },
				{ label: "Phone", value: clientPhone, href: clientPhone ? `tel:${clientPhone}` : null },
				{ label: "Destination", value: enquiry.location },
				{ label: "Travel date", value: travelLabel },
				{ label: "Nights", value: enquiry.nights ? String(enquiry.nights) : "" },
				{ label: "Rounds", value: enquiry.rounds ? String(enquiry.rounds) : "" },
				{ label: "Group size", value: enquiry.group_size ? String(enquiry.group_size) : "" },
				{ label: "Source", value: enquiry.inbound_channel || enquiry.site || "" },
		  ].filter((row) => row.value)
		: []

	return (
		<div className="package-modal-overlay is-form-modal">
			<div className="package-modal enquiry-view-modal" role="dialog" aria-modal="true" aria-label="Enquiry details">
				<div className="package-modal-header">
					<div>
						<h3>Enquiry details</h3>
						{clientName ? <p>{clientName}</p> : null}
					</div>
					<button type="button" className="package-modal-close" onClick={onClose} aria-label="Close">
						<X size={18} />
					</button>
				</div>

				<div className="package-modal-body">
					{loading ? (
						<div className="enquiry-view-loading">
							<Loading />
						</div>
					) : !enquiry ? (
						<p className="enquiry-view-empty">This enquiry could not be loaded.</p>
					) : (
						<div className="enquiry-view">
							{rows.length > 0 && (
								<div className="enquiry-view-table">
									{rows.map((row) => (
										<div className="enquiry-view-row" key={row.label}>
											<span className="enquiry-view-row-label">{row.label}</span>
											<span className="enquiry-view-row-value">{row.href ? <a href={row.href}>{row.value}</a> : row.value}</span>
										</div>
									))}
								</div>
							)}

							<div className="enquiry-view-notes">
								<span className="enquiry-view-notes-label">Client notes</span>
								{enquiry.notes ? <p>{enquiry.notes}</p> : <p className="is-muted">No notes were recorded on this enquiry.</p>}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}
